import { Copy, Download, FileText, Key, Play, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { useIsDark } from '../contexts/ThemeContext';

interface ApiTestingModalProps {
	isOpen: boolean;
	onClose: () => void;
	workflow: {
		id: string;
		name: string;
		slug?: string;
		dagDefinition: any;
	};
}

interface ApiKey {
	keyId: string;
	secret: string;
	scopes: string[];
	rateLimitRpm: number;
	rateLimitRph: number;
}

/**
 * API Testing Modal for HTTP Endpoint Workflows
 */
export function ApiTestingModal({ isOpen, onClose, workflow }: ApiTestingModalProps) {
	const [activeTab, setActiveTab] = useState<'upload' | 'test' | 'export'>('upload');
	const [apiKey, setApiKey] = useState<ApiKey | null>(null);
	const [inputData, setInputData] = useState<Record<string, any>>({});
	const [testResult, setTestResult] = useState<any>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [copiedItem, setCopiedItem] = useState<string | null>(null);
	const isDark = useIsDark();

	if (!isOpen) return null;

	// Extract HTTP endpoint configuration
	const httpEndpointNode = workflow.dagDefinition?.nodes?.find(
		(node: any) => node.type === 'start.http-endpoint'
	);
	const inputSchema = httpEndpointNode?.config?.inputSchema;
	const slug = httpEndpointNode?.config?.slug || workflow.slug || 'your-workflow';

	const handleApiKeyUpload = (file: File) => {
		const reader = new FileReader();
		reader.onload = e => {
			try {
				const keyData = JSON.parse(e.target?.result as string);
				setApiKey(keyData);
				setActiveTab('test');
			} catch (error) {
				alert('Invalid API key file. Please upload a valid JSON file.');
			}
		};
		reader.readAsText(file);
	};

	const generateCurlCommand = () => {
		const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
		const body = JSON.stringify({ input: inputData });
		const path = `/api/v1/flows/${slug}/execute`;

		return `curl -X POST "${baseUrl}${path}" \\
  -H "Content-Type: application/json" \\
  -H "x-api-key: ${apiKey?.keyId || 'YOUR_API_KEY'}" \\
  -d '${body}'`;
	};

	const generatePostmanCollection = () => {
		const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
		return {
			info: {
				name: `${workflow.name} API`,
				description: `Generated API collection for ${workflow.name}`,
				schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json',
			},
			item: [
				{
					name: workflow.name,
					request: {
						method: 'POST',
						header: [
							{ key: 'Content-Type', value: 'application/json' },
							{ key: 'x-api-key', value: '{{API_KEY}}' },
						],
						url: {
							raw: `${baseUrl}/api/v1/flows/${slug}/execute`,
							host: [baseUrl.replace(/https?:\/\//, '')],
							path: ['api', 'v1', 'flows', slug, 'execute'],
						},
						body: {
							mode: 'raw',
							raw: JSON.stringify({ input: inputData }, null, 2),
							options: { raw: { language: 'json' } },
						},
					},
				},
			],
		};
	};

	const testEndpoint = async () => {
		if (!apiKey) return;

		setIsLoading(true);
		try {
			const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
			const body = JSON.stringify({ input: inputData });
			const path = `/api/v1/flows/${slug}/execute`;

			// Simple API key authentication (no HMAC)
			const response = await fetch(`${baseUrl}${path}`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'x-api-key': apiKey.keyId,
				},
				body,
			});

			const result = await response.json();
			setTestResult({ status: response.status, data: result });
		} catch (error) {
			setTestResult({ status: 0, data: { error: error.message } });
		} finally {
			setIsLoading(false);
		}
	};

	const copyToClipboard = async (text: string, item: string) => {
		try {
			await navigator.clipboard.writeText(text);
			setCopiedItem(item);
			setTimeout(() => setCopiedItem(null), 2000);
		} catch (error) {
			console.error('Failed to copy to clipboard:', error);
		}
	};

	const downloadFile = (content: string, filename: string, type: string = 'text/plain') => {
		const blob = new Blob([content], { type });
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = filename;
		link.click();
		URL.revokeObjectURL(url);
	};

	return (
		<div className="flex fixed inset-0 z-50 justify-center items-center bg-black bg-opacity-50">
			<div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
				{/* Header */}
				<div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
					<div>
						<h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
							🧪 API Endpoint Tester
						</h2>
						<p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
							Test your HTTP endpoint:{' '}
							<code className="font-mono text-blue-600">{workflow.name}</code>
						</p>
					</div>
					<button
						onClick={onClose}
						className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
					>
						<X className="w-6 h-6" />
					</button>
				</div>

				{/* Tab Navigation */}
				<div className="flex border-b border-gray-200 dark:border-gray-700">
					{[
						{ id: 'upload', label: '🔑 Upload API Key', icon: Key },
						{ id: 'test', label: '🧪 Test Endpoint', icon: Play },
						{ id: 'export', label: '📤 Export', icon: Download },
					].map(tab => (
						<button
							key={tab.id}
							onClick={() => setActiveTab(tab.id as any)}
							className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors ${
								activeTab === tab.id
									? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
									: 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
							}`}
						>
							<tab.icon className="w-4 h-4" />
							{tab.label}
						</button>
					))}
				</div>

				<div className="p-6 overflow-y-auto max-h-[70vh]">
					{/* Step 1: Upload API Key */}
					{activeTab === 'upload' && (
						<div className="space-y-6">
							<div className="text-center">
								<Key className="mx-auto mb-4 w-16 h-16 text-blue-500" />
								<h3 className="mb-2 text-lg font-semibold">Upload Your API Key</h3>
								<p className="text-gray-600 dark:text-gray-400">
									Upload the API key JSON file you downloaded when creating your
									API key
								</p>
							</div>

							{!apiKey ? (
								<div
									onDrop={e => {
										e.preventDefault();
										const files = Array.from(e.dataTransfer.files);
										if (files[0]) handleApiKeyUpload(files[0]);
									}}
									onDragOver={e => e.preventDefault()}
									className="p-8 text-center rounded-lg border-2 border-blue-300 border-dashed transition-colors dark:border-blue-600 hover:border-blue-400"
								>
									<Upload className="mx-auto mb-4 w-12 h-12 text-blue-400" />
									<p className="mb-2 text-lg font-medium text-gray-900 dark:text-gray-100">
										Drag & drop your API key file
									</p>
									<p className="mb-4 text-gray-600 dark:text-gray-400">
										Or click to browse for api-key-*.json file
									</p>
									<input
										type="file"
										accept=".json"
										onChange={e =>
											e.target.files?.[0] &&
											handleApiKeyUpload(e.target.files[0])
										}
										className="hidden"
										id="api-key-upload"
									/>
									<label
										htmlFor="api-key-upload"
										className="inline-flex items-center px-4 py-2 text-white bg-blue-600 rounded cursor-pointer hover:bg-blue-700"
									>
										Browse Files
									</label>
								</div>
							) : (
								<div className="p-6 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
									<div className="flex justify-between items-center mb-4">
										<h4 className="text-lg font-medium text-green-900 dark:text-green-100">
											✅ API Key Loaded
										</h4>
										<button
											onClick={() => setApiKey(null)}
											className="text-green-600 hover:text-green-700"
										>
											Change Key
										</button>
									</div>
									<div className="space-y-2 text-sm">
										<div>
											<strong>Key ID:</strong>{' '}
											<code className="font-mono">{apiKey.keyId}</code>
										</div>
										<div>
											<strong>Scopes:</strong> {apiKey.scopes.join(', ')}
										</div>
										<div>
											<strong>Rate Limit:</strong> {apiKey.rateLimitRpm}/min,{' '}
											{apiKey.rateLimitRph}/hour
										</div>
									</div>
									<button
										onClick={() => setActiveTab('test')}
										className="flex gap-2 justify-center items-center px-4 py-2 mt-4 w-full text-white bg-green-600 rounded hover:bg-green-700"
									>
										<Play className="w-4 h-4" />
										Start Testing
									</button>
								</div>
							)}
						</div>
					)}

					{/* Step 2: Test Endpoint */}
					{activeTab === 'test' && (
						<div className="space-y-6">
							{!apiKey ? (
								<div className="py-8 text-center">
									<Key className="mx-auto mb-4 w-12 h-12 text-gray-400" />
									<p className="text-gray-600 dark:text-gray-400">
										Please upload an API key first
									</p>
									<button
										onClick={() => setActiveTab('upload')}
										className="px-4 py-2 mt-4 text-white bg-blue-600 rounded hover:bg-blue-700"
									>
										Upload API Key
									</button>
								</div>
							) : (
								<>
									<div>
										<h3 className="mb-4 text-lg font-semibold">
											Test Your API Endpoint
										</h3>
										<div className="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
											<div className="font-mono text-sm text-blue-800 dark:text-blue-200">
												POST /api/v1/flows/{slug}/execute
											</div>
										</div>
									</div>

									{/* Input Form */}
									<div>
										<h4 className="mb-3 font-medium text-md">Request Data</h4>
										{inputSchema?.type === 'simple' && inputSchema.fields ? (
											<div className="space-y-3">
												{inputSchema.fields.map(
													(field: any, index: number) => (
														<div key={index}>
															<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
																{field.name} ({field.type})
																{field.required && (
																	<span className="ml-1 text-red-500">
																		*
																	</span>
																)}
															</label>
															<input
																type={
																	field.type === 'number'
																		? 'number'
																		: 'text'
																}
																value={inputData[field.name] || ''}
																onChange={e =>
																	setInputData(prev => ({
																		...prev,
																		[field.name]:
																			field.type === 'number'
																				? Number(
																						e.target
																							.value
																				  )
																				: e.target.value,
																	}))
																}
																placeholder={`Enter ${field.name}...`}
																className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
															/>
															{field.description && (
																<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
																	{field.description}
																</p>
															)}
														</div>
													)
												)}
											</div>
										) : (
											<div>
												<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
													Input Data (JSON)
												</label>
												<textarea
													value={JSON.stringify(inputData, null, 2)}
													onChange={e => {
														try {
															setInputData(
																JSON.parse(e.target.value)
															);
														} catch {
															// Invalid JSON, ignore
														}
													}}
													rows={6}
													className="px-3 py-2 w-full font-mono text-sm text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
													placeholder='{"userId": "123", "status": "active"}'
												/>
											</div>
										)}
									</div>

									{/* Test Button */}
									<button
										onClick={testEndpoint}
										disabled={isLoading}
										className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition-colors ${
											isLoading
												? 'bg-gray-400 cursor-not-allowed'
												: 'bg-green-600 hover:bg-green-700'
										} text-white`}
									>
										{isLoading ? (
											<>
												<div className="w-4 h-4 rounded-full border-2 border-white animate-spin border-t-transparent" />
												Testing...
											</>
										) : (
											<>
												<Play className="w-4 h-4" />
												Test API Endpoint
											</>
										)}
									</button>

									{/* Test Result */}
									{testResult && (
										<div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
											<div className="flex justify-between items-center mb-3">
												<h4 className="font-medium">Response</h4>
												<span
													className={`px-2 py-1 rounded text-xs font-medium ${
														testResult.status === 200
															? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200'
															: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
													}`}
												>
													{testResult.status === 200
														? 'Success'
														: 'Error'}{' '}
													({testResult.status})
												</span>
											</div>
											<pre className="overflow-x-auto p-3 text-xs text-gray-100 bg-gray-900 rounded">
												<code>
													{JSON.stringify(testResult.data, null, 2)}
												</code>
											</pre>
										</div>
									)}
								</>
							)}
						</div>
					)}

					{/* Step 3: Export */}
					{activeTab === 'export' && (
						<div className="space-y-6">
							<div>
								<h3 className="mb-4 text-lg font-semibold">
									Export API Documentation
								</h3>
								<p className="mb-6 text-gray-600 dark:text-gray-400">
									Generate ready-to-use API documentation and testing tools
								</p>
							</div>

							<div className="grid gap-4">
								{/* cURL Command */}
								<div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
									<div className="flex justify-between items-center mb-3">
										<h4 className="font-medium">cURL Command</h4>
										<button
											onClick={() =>
												copyToClipboard(generateCurlCommand(), 'curl')
											}
											className="flex gap-2 items-center px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
										>
											<Copy className="w-4 h-4" />
											{copiedItem === 'curl' ? 'Copied!' : 'Copy'}
										</button>
									</div>
									<pre className="overflow-x-auto p-3 text-xs text-gray-100 bg-gray-900 rounded">
										<code>{generateCurlCommand()}</code>
									</pre>
								</div>

								{/* Postman Collection */}
								<div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
									<div className="flex justify-between items-center mb-3">
										<h4 className="font-medium">Postman Collection</h4>
										<div className="flex gap-2">
											<button
												onClick={() =>
													copyToClipboard(
														JSON.stringify(
															generatePostmanCollection(),
															null,
															2
														),
														'postman'
													)
												}
												className="flex gap-2 items-center px-3 py-1 text-white bg-orange-600 rounded hover:bg-orange-700"
											>
												<Copy className="w-4 h-4" />
												{copiedItem === 'postman' ? 'Copied!' : 'Copy JSON'}
											</button>
											<button
												onClick={() =>
													downloadFile(
														JSON.stringify(
															generatePostmanCollection(),
															null,
															2
														),
														`${workflow.name}-api.postman_collection.json`,
														'application/json'
													)
												}
												className="flex gap-2 items-center px-3 py-1 text-white bg-orange-600 rounded hover:bg-orange-700"
											>
												<Download className="w-4 h-4" />
												Download
											</button>
										</div>
									</div>
									<p className="mb-2 text-xs text-gray-600 dark:text-gray-400">
										Import this collection into Postman to test your API
									</p>
									<div className="p-3 text-xs text-gray-100 bg-gray-900 rounded">
										<div>1. Copy or download the collection JSON</div>
										<div>2. Open Postman → Import → Paste JSON</div>
										<div>
											3. Set environment variables for API_KEY and
											HMAC_SIGNATURE
										</div>
									</div>
								</div>

								{/* API Documentation */}
								<div className="p-4 bg-gray-50 rounded-lg dark:bg-gray-800">
									<div className="flex justify-between items-center mb-3">
										<h4 className="font-medium">API Documentation</h4>
										<button
											onClick={() => {
												const docs = `# ${workflow.name} API Documentation

## Endpoint
\`POST /api/v1/flows/${slug}/execute\`

## Authentication
- API Key (x-api-key header)
- HMAC-SHA256 signature (x-signature header)
- Timestamp (x-timestamp header)
- Nonce (x-nonce header)

## Request Format
\`\`\`json
${JSON.stringify(
	{
		input:
			inputSchema?.type === 'simple'
				? Object.fromEntries(
						inputSchema.fields?.map((f: any) => [f.name, `<${f.type}>`]) || []
				  )
				: {},
	},
	null,
	2
)}
\`\`\`

## Example Response
\`\`\`json
{
  "output": {
    "statusCode": 200,
    "data": []
  },
  "executionId": "uuid",
  "responseNodeId": "response-node"
}
\`\`\`
`;
												downloadFile(
													docs,
													`${workflow.name}-api-docs.md`,
													'text/markdown'
												);
											}}
											className="flex gap-2 items-center px-3 py-1 text-white bg-blue-600 rounded hover:bg-blue-700"
										>
											<FileText className="w-4 h-4" />
											Download Docs
										</button>
									</div>
									<p className="text-xs text-gray-600 dark:text-gray-400">
										Complete API documentation in Markdown format
									</p>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
